// Negative Prompt Card - clean coverflow, same style as intent cards
// Each card represents a category. Clicking toggles the whole category on/off.
// Custom card has a textarea for free-text input.
// Ported from studio: imports adjusted for workspace structure

import { fetchNegativeCards } from '../create/cards/api.js'
import { updateCoverflow } from '../../utils/coverflow.js'
import { esc } from '../../utils/escape.js'

// Hardcoded fallback used when the API returns 0 results
const FALLBACK_NEG_CARDS = [
  { id: 'quality',     title: 'Quality',     desc: 'Blur, noise, exposure',    tags: ['blurry', 'low quality', 'noise', 'overexposed', 'underexposed'] },
  { id: 'artifacts',   title: 'Artifacts',   desc: 'Distortion, watermarks',   tags: ['distorted', 'artifacts', 'watermark', 'text overlay', 'jpeg artifacts'] },
  { id: 'composition', title: 'Composition', desc: 'Framing, cropping issues', tags: ['cropped', 'off-center', 'bad framing', 'tilted', 'cut off'] },
  { id: 'style',       title: 'Style',       desc: 'Color, lighting issues',   tags: ['wrong style', 'inconsistent color', 'oversaturated', 'flat lighting'] },
]

// Custom card is always appended client-side, never from API
const CUSTOM_CARD = { id: 'custom', title: 'Custom', desc: 'Your own terms', tags: [] }

// Mutable working list - starts as fallback, upgraded from API in initNegativePromptCard
let _negCards = [...FALLBACK_NEG_CARDS, CUSTOM_CARD]

// Which category cards are toggled on
const _on = new Set()

let _negIdx = Math.floor(_negCards.length / 2)

// -- Tag normalization --------------------------------------------------------

/**
 * Normalize tags from either an API card or a hardcoded card.
 *   API card:       card.tags may be a JSON array string e.g. '["blurry","noise"]'
 *   API fallback:   use card.description split by comma
 *   Hardcoded card: card.tags is already a plain string[]
 * @param {{tags?: string|string[], description?: string, desc?: string}} card
 * @returns {string[]}
 */
function _normalizeTags(card) {
  if (Array.isArray(card.tags)) return card.tags
  if (typeof card.tags === 'string') {
    try { return JSON.parse(card.tags) } catch { /* fall through */ }
  }
  const src = card.description || card.desc || ''
  return src ? src.split(',').map(s => s.trim()).filter(Boolean) : []
}

// -- Card HTML ----------------------------------------------------------------

function _cardHTML(c, i) {
  const isOn = _on.has(c.id)
  if (c.id === 'custom') {
    return `
      <div class="neg-card${isOn ? ' neg-card--on' : ''}" data-neg-idx="${i}">
        <div class="neg-card-main">
          <div class="neg-card-text">
            <span class="neg-card-title">${esc(c.title)}</span>
            <span class="neg-card-desc">${esc(c.desc)}</span>
          </div>
        </div>
        <textarea class="neg-custom-textarea" id="negCustomTextarea" placeholder="Describe what to avoid..." rows="2" onclick="event.stopPropagation()"></textarea>
      </div>`
  }
  return `
    <div class="neg-card${isOn ? ' neg-card--on' : ''}" data-neg-idx="${i}">
      <div class="neg-card-main">
        <div class="neg-card-text">
          <span class="neg-card-title">${esc(c.title)}</span>
          <span class="neg-card-desc">${esc(c.desc)}</span>
        </div>
        <span class="neg-card-badge${isOn ? ' neg-card-badge--on' : ''}">${isOn ? 'ON' : _normalizeTags(c).length}</span>
      </div>
    </div>`
}

// -- Coverflow position update ------------------------------------------------

function _updateNegCoverflow(container) {
  updateCoverflow(container, '.neg-card', _negIdx, {
    scale:       0.82,
    rotateX:     18,
    opacity:     0.28,
    translateY:  72,
    activeClass: true,
  })
  // Textarea only interactive when its card is centered
  container.querySelectorAll('.neg-card').forEach((card, i) => {
    const ta = card.querySelector('.neg-custom-textarea')
    if (ta) ta.style.pointerEvents = i === _negIdx ? 'auto' : 'none'
  })
}

// -- Render + bind (called once with fallback, again if API upgrades) ---------

function _renderNegCards(col) {
  col.innerHTML = `
    <div class="neg-cards-section">
      <div class="neg-cards-coverflow">
        <div class="neg-cards-track" id="negCardsTrack">
          ${_negCards.map((c, i) => _cardHTML(c, i)).join('')}
        </div>
      </div>
      <div class="neg-cards-nav">
        <button class="neg-nav-btn neg-nav-up" title="Previous">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M18 15l-6-6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <button class="neg-nav-btn neg-nav-down" title="Next">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>
    </div>`

  const track = col.querySelector('#negCardsTrack')
  _updateNegCoverflow(col)

  // Card click - navigate or toggle
  track?.addEventListener('click', e => {
    if (e.target.closest('.neg-custom-textarea')) return
    const card = e.target.closest('.neg-card')
    if (!card) return
    const idx = parseInt(card.dataset.negIdx)
    if (isNaN(idx)) return
    if (idx === _negIdx) {
      // Toggle on/off
      const c = _negCards[idx]
      if (_on.has(c.id)) _on.delete(c.id)
      else _on.add(c.id)
      card.classList.toggle('neg-card--on', _on.has(c.id))
      const badge = card.querySelector('.neg-card-badge')
      if (badge) {
        badge.classList.toggle('neg-card-badge--on', _on.has(c.id))
        badge.textContent = _on.has(c.id) ? 'ON' : _normalizeTags(c).length
      }
    } else {
      _negIdx = idx
      _updateNegCoverflow(col)
    }
  })

  col.querySelector('.neg-nav-up')?.addEventListener('click', () => {
    if (_negIdx > 0) { _negIdx--; _updateNegCoverflow(col) }
  })
  col.querySelector('.neg-nav-down')?.addEventListener('click', () => {
    if (_negIdx < _negCards.length - 1) { _negIdx++; _updateNegCoverflow(col) }
  })

  track?.addEventListener('wheel', e => {
    e.preventDefault()
    if (e.deltaY > 0 && _negIdx < _negCards.length - 1) _negIdx++
    else if (e.deltaY < 0 && _negIdx > 0) _negIdx--
    _updateNegCoverflow(col)
  }, { passive: false })
}

// -- Init ---------------------------------------------------------------------

export async function initNegativePromptCard() {
  const col = document.getElementById('negativeCardsCol')
  if (!col) return

  // Render immediately with fallback - not blocked on network
  _on.clear()
  _negCards = [...FALLBACK_NEG_CARDS, CUSTOM_CARD]
  _negIdx = Math.floor(_negCards.length / 2)
  _renderNegCards(col)

  // Attempt to upgrade from API in the background
  try {
    const apiCards = await fetchNegativeCards()
    if (apiCards.length > 0) {
      _negCards = [
        ...apiCards.map(c => ({
          id:    c.id,
          title: c.title,
          desc:  c.description || c.desc || '',
          tags:  c.tags,  // _normalizeTags handles string or array
        })),
        CUSTOM_CARD,
      ]
      _on.clear()
      _negIdx = Math.floor(_negCards.length / 2)
      _renderNegCards(col)
    }
    // apiCards.length === 0: keep the fallback render, nothing to do
  } catch {
    // API failed - fallback render is already displayed
  }
}

// -- Collect negative prompt for generation -----------------------------------

export function getNegativePrompt() {
  const tags = _negCards
    .filter(c => c.id !== 'custom' && _on.has(c.id))
    .flatMap(c => _normalizeTags(c))
  const custom = document.getElementById('negCustomTextarea')?.value?.trim() || ''
  return [...tags, ...(custom ? [custom] : [])].join(', ')
}
